import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Rocket, Server, ShieldCheck } from 'lucide-react';
import { hostedSites } from '../data/hostingSites';
import { HOSTING_PRICE_MIN, PLAN_COUNT } from '../data/hostingPlans';
import { DEVELOPMENT_TIERS, formatUSD } from '../lib/quoteEstimator';
import HScroller from '../components/ui/HScroller';

const STACK = [
  {
    icon: Server,
    title: 'Reliable infrastructure',
    text: 'Apache-backed hosting on hardened Ubuntu servers with automated health checks, log rotation, and backup schedules.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure by default',
    text: 'TLS everywhere, security headers, Fail2Ban intrusion prevention, and ModSecurity WAF protection.',
  },
  {
    icon: Rocket,
    title: 'Modern stack',
    text: 'React SPAs, static sites, or full-stack apps — we handle Apache vhosts, SSL certificates, and CI/CD integration.',
  },
];

const hostOf = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const Home: React.FC = () => {
  const live = hostedSites.filter((s) => s.status === 'live' && s.url && s.name !== 'FairCorp Hosting & Development');

  return (
    <div className="page-inner pb-10">
      <section aria-labelledby="hero-title" className="mt-4 rounded-3xl border border-primary-500/30 bg-gradient-to-br from-secondary-900 via-primary-950/80 to-secondary-950 px-5 py-8 sm:px-10 sm:py-12">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-300">FairCorp</p>
        <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <h1 id="hero-title" className="text-3xl sm:text-5xl font-bold tracking-tight text-white">
              FairCorp Hosting &amp; Development
            </h1>
            <p className="mt-3 max-w-xl text-base sm:text-lg text-secondary-200">
              Two services, priced apart. Commission a site or application, or put a site you already have on managed hosting. One-time development and monthly hosting are always separate contracts.
            </p>
          </div>
          <img
            src="/mono-website-hosting.png"
            alt=""
            width={160}
            height={160}
            className="hidden sm:block w-32 h-32 lg:w-40 lg:h-40 object-contain opacity-90"
          />
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <Link to="/pricing" className="btn btn-primary">
            Development pricing <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
          <Link to="/plans" className="btn btn-outline">
            Hosting plans
          </Link>
        </div>
      </section>

      <section aria-labelledby="two-services" className="mt-6 grid gap-3 sm:grid-cols-2">
        <h2 id="two-services" className="sr-only">
          Two services, kept separate
        </h2>
        <Link
          to="/pricing"
          className="group panel p-4 sm:p-5 hover:border-primary-400/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        >
          <span className="chip-dev">Development · one-time</span>
          <p className="mt-3 text-xl font-bold text-white">Development pricing</p>
          <p className="text-sm font-semibold text-primary-200">From {formatUSD(DEVELOPMENT_TIERS.T1.min)}</p>
          <p className="mt-2 text-sm text-secondary-300">
            Compare all seven tiers and the reasons a project lands in each, then use the plain-language calculator for your own estimate. Runs in your browser; nothing is saved.
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-300 group-hover:text-white">
            See pricing <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </span>
        </Link>
        <Link
          to="/plans"
          className="group panel p-4 sm:p-5 hover:border-accent-400/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        >
          <span className="chip-host">Hosting · monthly</span>
          <p className="mt-3 text-xl font-bold text-white">Hosting plans</p>
          <p className="text-sm font-semibold text-accent-200">From {formatUSD(HOSTING_PRICE_MIN)}/mo</p>
          <p className="mt-2 text-sm text-secondary-300">
            {PLAN_COUNT} approved plans in six categories. H1–H4 are available now; H5 hosting is not available at this time. Pick by what you run — no development questions, and no FairCorp build required.
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-200 group-hover:text-white">
            Find a plan <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </span>
        </Link>
      </section>
      <p className="mt-3 text-sm text-secondary-300">
        Need both? Add optional hosting at step 6 of the calculator. Development and hosting appear on separate lines and are separate contracts.
      </p>

      <section aria-labelledby="rebuild" className="mt-6 panel p-4 sm:p-5 border-primary-400/30">
        <h2 id="rebuild" className="text-lg font-bold text-white">Already have a website?</h2>
        <p className="mt-2 text-sm text-secondary-300">
          If you own substantial source code, development is 50% of the tier's quoted price. If the source code is lost and the site is still online, FairCorp can rebuild it from a webscrape at 70% of the tier's quoted price. The discount covers development only; hosting and optional extras stay separate.
        </p>
        <Link to="/pricing" className="mt-3 inline-flex items-center min-h-[44px] text-sm font-semibold text-primary-300 hover:text-white">
          See the rebuild pricing <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
        </Link>
      </section>

      <section aria-labelledby="pricing-difference" className="mt-4 panel p-4 sm:p-5">
        <h2 id="pricing-difference" className="text-lg font-bold text-white">How this pricing works</h2>
        <p className="mt-2 text-sm text-secondary-300">
          FairCorp publishes fixed tier bands and a read-only calculator you can run yourself. You see the band before anyone writes a custom proposal. Tier 4 and above still need an engineering quote for the final figure. That is different from a process that starts from an opaque custom quote with no published band.
        </p>
      </section>

      <section aria-labelledby="stack" className="mt-8">
        <h2 id="stack" className="text-lg font-bold text-white mb-3">
          What’s behind the hosting
        </h2>
        <ul className="panel divide-y divide-secondary-800">
          {STACK.map(({ icon: Icon, title, text }) => (
            <li key={title} className="flex gap-3 p-4">
              <Icon className="w-5 h-5 mt-0.5 shrink-0 text-primary-300" aria-hidden="true" />
              <div>
                <p className="font-semibold text-white">{title}</p>
                <p className="text-sm text-secondary-300">{text}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="hosted" className="mt-8">
        <div className="flex items-baseline justify-between gap-3 mb-3">
          <h2 id="hosted" className="text-lg font-bold text-white">
            Hosted by FairCorp
          </h2>
          <Link to="/sites" className="inline-flex items-center min-h-[44px] text-sm font-semibold text-primary-300 hover:text-white">
            All sites <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
          </Link>
        </div>
        <HScroller label="Live hosted sites" trackClassName="gap-3 pr-4">
          {live.map((s) => (
            <a
              key={s.name}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="snap-start shrink-0 w-64 panel p-4 hover:border-primary-400/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            >
              <p className="flex items-center justify-between gap-2 font-semibold text-white">
                <span className="truncate">{s.name.trim()}</span>
                <ExternalLink className="w-4 h-4 shrink-0 text-secondary-400" aria-hidden="true" />
              </p>
              <p className="text-xs text-primary-300 truncate">{hostOf(s.url!)}</p>
              <p className="mt-2 text-sm text-secondary-300 line-clamp-3">{s.description}</p>
            </a>
          ))}
        </HScroller>
      </section>
    </div>
  );
};

export default Home;
